import { useState } from 'react';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger, Avatar } from '@scripsteam/scrips-signal-ds';

/**
 * PracticeSwitcher — the active-practice menu in the global top bar, as the Practice app draws it (design log 15 Oct,
 * "make practice switcher like PM"): the trigger is the practice's picture, its name and a chevron; the menu lists every
 * practice as a rich row (picture, name with a Primary tag, address, today's hours), the active one tinted and ticked,
 * and, for roles that can open it, a "Practice page" button on the row that opens that practice's settings.
 *
 * Controlled, never self-fetching: the top bar (`top-bar-practice.tsx`) owns the list and writes the choice to both
 * the scheduling store and the auth store (the api-client's PracticeId header, PROD-1190 / PROD-1478).
 */
export interface PracticeSwitcherPractice {
  id: string;
  name: string;
  imageUrl?: string | null;
  address?: string | null;
  isPrimary?: boolean;
  /** "Open today 08:00 – 18:00" / "Closed today"; null when the practice has no hours on file. */
  hoursLine?: string | null;
}

interface PracticeSwitcherProps {
  practices: PracticeSwitcherPractice[];
  practiceId?: string;
  onPractice: (id: string) => void;
  /** A practice's settings page, from its row. Omitted: no "Practice page" button (roles that cannot open it). */
  onOpenSettings?: (id: string) => void;
}

export function PracticeSwitcher({ practices, practiceId, onPractice, onOpenSettings }: PracticeSwitcherProps) {
  const [open, setOpen] = useState(false);
  const active = practices.find((p) => p.id === practiceId);
  const label = active ? active.name || '—' : 'Select practice';
  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`Active practice: ${label}`} className="practice-trigger" />
        }
      >
        <PracticeAvatar name={label} imageUrl={active?.imageUrl ?? null} />
        <span className="practice-trigger-name">{label}</span>
        <ChevronDown aria-hidden className="practice-trigger-chevron" />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent align="end" side="bottom" className="practice-menu" role="menu" aria-label="Practices">
        {practices.map((p) => {
          const selected = p.id === practiceId;
          const detail = [p.hoursLine].filter(Boolean).join(' · ') || 'No hours set for today';
          return (
            <div key={p.id} className="practice-row" data-selected={selected ? '' : undefined}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className="practice-row-main"
                onClick={() => {
                  setOpen(false);
                  onPractice(p.id);
                }}
              >
                <PracticeAvatar name={p.name} imageUrl={p.imageUrl ?? null} large />
                <span className="practice-row-text">
                  <span className="practice-row-title">
                    <span className="practice-row-name">{p.name || '—'}</span>
                    {p.isPrimary ? <span className="practice-row-tag">Primary</span> : null}
                  </span>
                  {p.address?.trim() ? <span className="practice-row-address">{p.address.trim()}</span> : null}
                  <span className="practice-row-meta">{detail}</span>
                </span>
                {selected ? <Check aria-hidden className="practice-row-check" /> : null}
              </button>
              {onOpenSettings ? (
                <button
                  type="button"
                  aria-label={`Open ${p.name || 'the practice'} page`}
                  className="practice-row-settings"
                  onClick={() => {
                    setOpen(false);
                    onOpenSettings(p.id);
                  }}
                >
                  Practice page
                  <ArrowRight aria-hidden />
                </button>
              ) : null}
            </div>
          );
        })}
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

function PracticeAvatar({ name, imageUrl, large = false }: { name: string; imageUrl: string | null; large?: boolean }) {
  const initial = name.trim().charAt(0).toUpperCase() || 'P';
  return (
    <Avatar size={large ? 'md' : 'xs'} className="shrink-0" src={imageUrl ?? undefined} name={name} initials={initial} alt={name} />
  );
}
